import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';

// SVG Icons
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// New icons for maximize and minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

// Box icon for batch modal
const BoxIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

// Barcode icon for serial modal
const BarcodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
  </svg>
);

const API_URL = '/api';

// Batch Info Modal Component
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BoxIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? dayjs(batch.ExpiryDate || batch.expiryDate).format('YYYY-MM-DD') 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BoxIcon className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Info Modal Component
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BarcodeIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {serial}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BarcodeIcon className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockInViewModal = ({ open, onClose, stockInId }) => {
  const [stockInDetails, setStockInDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    locations: [],
  });
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize

  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentItemBatches, setCurrentItemBatches] = useState([]);
  const [currentItemSerials, setCurrentItemSerials] = useState([]);
  const [currentItemName, setCurrentItemName] = useState('');
  const [currentItemLocation, setCurrentItemLocation] = useState('');

  // ---- Department screen-config state ----
  const SCREEN_NAME = 'StockIn';
  const [screenConfig, setScreenConfig] = useState(null);

  useEffect(() => {
    if (open) {
      fetchDropdownData();
      (async () => {
        try {
          const cfg = await fetchScreenConfig();
          setScreenConfig(cfg);
        } catch (e) {
          setScreenConfig(null);
        }
      })();
      // Reset maximize state when modal opens
      setIsMaximized(false);

      if (stockInId?.StockInID || stockInId?.stockInId) {
        fetchStockInDetails(stockInId.StockInID || stockInId.stockInId);
      }
    }
  }, [open, stockInId]);

  const fetchDropdownData = async () => {
    try {
      const response = await axios.get(`${API_URL}/stockin/form-data`);
      setDropdownData({
        users: response.data.users || [],
        locations: response.data.locations || [],
      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) { /* ignore */ }
        const msg = body && body.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }

      const data = await res.json();
      return Array.isArray(data) ? (data[0] || null) : data;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      throw err;
    }
  };

  // Helper truthy & show-checkers (Department feature)
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  // Helper to determine if Department should show in header
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen; // BOTH required
  };

  // Helper to determine if Department should show in items
  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem; // BOTH required
  };

  const fetchStockInDetails = async (stockInID) => {
    setLoading(true);
    setError(null);
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/stockin/${stockInID}?t=${timestamp}`);
      setStockInDetails(response.data);
    } catch (error) {
      console.error('Error fetching StockIn details:', error);
      setError('Failed to load stock in details');
      setStockInDetails(null);
    }
    setLoading(false);
  };

  // Helper function to get location name
  const getLocationName = (locationCode) => {
    if (!locationCode) return '-';
    const location = dropdownData.locations.find(loc => loc.LocationCode === locationCode);
    return location ? location.LocationName : locationCode;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(2);
  };

  // Toggle maximize/minimize state
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Function to open batch modal
  const openBatchModal = (item) => {
    setCurrentItemBatches(item.Batches || item.batches || []);
    setCurrentItemName(item.ItemName || item.itemName || '');
    setShowBatchModal(true);
  };

  // Function to open serial modal
  const openSerialModal = (item) => {
    setCurrentItemSerials(item.SerialNumbers || item.serialNumbers || []);
    setCurrentItemName(item.ItemName || item.itemName || '');
    setCurrentItemLocation(item.LocationCode || item.locationCode || '');
    setShowSerialModal(true);
  };

  if (!open || !stockInId) return null;

  // Use stockInDetails if available, otherwise use stockInId
  const stockIn = stockInDetails || stockInId;
  const stockInItems = stockInDetails?.Items || stockInId?.Items || [];

  // Inline status determination
  const isActive = stockIn.Status === 1 || stockIn.Status === true;
  const statusText = isActive ? 'Active' : 'Inactive';
  const statusStyle = isActive
    ? 'bg-green-100 text-green-800'
    : 'bg-yellow-100 text-yellow-800';

  // Inventory flag determination
  const hasInventoryFlag = stockIn.InventoryFlag === 1 || stockIn.InventoryFlag === true;
  const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';
  const inventoryFlagStyle = hasInventoryFlag
    ? 'bg-blue-100 text-blue-800'
    : 'bg-gray-100 text-gray-800';

  return (
    <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      <div
         className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
            transition-transform transform-gpu duration-300 ease-in-out
            ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Stock In Details
            </h2>
            <div className="flex space-x-2">
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                onClick={onClose}
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading stock in details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
              Error: {error}
            </div>
          ) : (
            <div className="space-y-6 pb-6">
              {/* StockIn Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">
                  Stock In Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Stock In ID
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {stockIn.StockInID || stockIn.stockInId || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Stock In Date
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {stockIn.StockInDate ? dayjs(stockIn.StockInDate).format('YYYY-MM-DD') : '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Added By User ID
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {stockIn.AddedByUserID || stockIn.addedByUserId || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Added By User Name
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                      {stockIn.AddedByUserName || stockIn.addedByUserName || '-'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Branch
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {stockIn.BranchName || stockIn.branchName || '-'} ({stockIn.BranchCode || stockIn.branchCode || '-'})
                    </div>
                  </div>

                  {/* Department in Header (controlled by screen-config) */}
                  {shouldShowDepartmentOnHeader(screenConfig) &&
                    (stockIn.DepartmentName || stockIn.DepartmentCode || stockIn.DepartmentID) && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-[#64748b]">Department</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                          {stockIn.DepartmentName
                            ? `${stockIn.DepartmentCode ? stockIn.DepartmentCode + ' - ' : ''}${stockIn.DepartmentName}`
                            : (stockIn.DepartmentCode || stockIn.DepartmentID || '-')}
                        </div>
                      </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Status
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>
                        {statusText}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Add to Inventory
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${inventoryFlagStyle}`}>
                        {inventoryFlagText}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Total Quantity
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                      {stockIn.TotalQuantity || stockIn.totalQuantity || '0'}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Note
                    </label>
                    <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px] uppercase">
                      {stockIn.Note || stockIn.note || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* StockIn Items */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">
                    Stock In Items
                  </h3>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Item Code</th>
                        <th className="p-2">Item Name</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Quantity Added</th>
                        <th className="p-2">Rate</th>
                        <th className="p-2">Value</th>
                        <th className="p-2">Warehouse</th>
                        {shouldShowDepartmentOnItems(screenConfig) && (
                          <th className="p-2">Department</th>
                        )}
                        <th className="p-2">Tracking Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockInItems.length > 0 ? (
                        stockInItems.map((item, idx) => {
                          // Determine tracking type
                          const hasBatches = item.Batches && item.Batches.length > 0;
                          const hasSerials = item.SerialNumbers && item.SerialNumbers.length > 0;
                          
                          return (
                            <tr key={idx} className="border-t border-[#E2E8F0]">
                              <td className="p-2 text-center">{idx + 1}</td>
                              <td className="p-2">
                                <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {item.ItemCode || item.itemCode || '-'}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.ItemName || item.itemName || '-'}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {item.UOM || item.uom || '-'}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {item.QuantityAdded || item.quantityAdded || '0'}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                  {formatCurrency(item.Rate || item.rate)}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] font-semibold">
                                  {formatCurrency(item.Value || item.value)}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                  {getLocationName(item.LocationCode || item.locationCode)}
                                </div>
                              </td>
                              {shouldShowDepartmentOnItems(screenConfig) && (
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.DepartmentCode || '-'}
                                  </div>
                                </td>
                              )}
                              <td className="p-2">
                                <div className="flex flex-col space-y-1">
                                  {hasBatches && (
                                    <button
                                      type="button"
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      onClick={() => openBatchModal(item)}
                                    >
                                      View Batches
                                    </button>
                                  )}
                                  {hasSerials && (
                                    <button
                                      type="button"
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      onClick={() => openSerialModal(item)}
                                    >
                                      View Serials
                                    </button>
                                  )}
                                  {!hasBatches && !hasSerials && (
                                    <span className="text-xs text-gray-500 text-center">No tracking</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={shouldShowDepartmentOnItems(screenConfig) ? 10 : 9} className="text-center text-gray-500 py-4">
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Total Summary Section */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">
                    Total Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Quantity</span>
                      <span className="text-base font-semibold">
                        {stockIn.TotalQuantity || stockIn.totalQuantity || '0'}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Value</span>
                      <span className="text-base font-semibold text-blue-600">
                        {formatCurrency(
                          stockInItems.reduce((sum, item) => {
                            const value = parseFloat(item.Value || item.value) || 0;
                            return sum + value;
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Entries Section - Only show if InventoryFlag is true */}
              
            </div>
          )}
        </div>

        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Batch Info Modal */}
      <BatchInfoModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        batches={currentItemBatches}
        productName={currentItemName}
      />

      {/* Serial Info Modal */}
      <SerialInfoModal
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        serialNumbers={currentItemSerials}
        productName={currentItemName}
        locationCode={currentItemLocation}
      />
    </div>
  );
};

export default StockInViewModal;